import { Injectable, inject } from '@angular/core';
import { catchError, firstValueFrom, map, of, timeout } from 'rxjs';
import { PersonImportApiService, type ImportRun, type SettleWavesAnswer } from '../infrastructure/person-import-api.service';
import { SETTLED_RUN_STATUSES } from './photos-processing-gate.service';

/** Экран ждёт досчёт перед первым кадром, поэтому потолок короткий: лучше открыться без имён и дать
 *  их дожать крону, чем держать человека перед пустотой. */
const SETTLE_CEILING_MS = 2500;

/** Окно памяти по проекту: проверка роли перезапускается на каждый вход в редактор, включая тёплый
 *  возврат, и цикл «редактор → Фотографии → редактор» стоит два POST на пустом месте. Число — та же
 *  величина, какой в этих же экранах уже меряют «как часто имеет смысл дёргать ростер проекта»
 *  (троттлинг перечита по возврату фокуса на «Лицах»). Верхней границы у числа нет и не нужно:
 *  окно закрывает только ответ, в котором волн в работе не осталось, поэтому длиной окна перечит по
 *  дописавшейся волне не съесть. Ошибка в меньшую сторону стоит лишнего POST и ничего больше. */
const SETTLE_MEMO_WINDOW_MS = 5000;

/** Итог досчёта: список прогонов — сервер ответил (пустой значит «дожимать было нечего»);
 *  `undefined` — ответа у нас нет, какой бы ни была причина, и делать по нему нечего. */
export type NameWaveSettleOutcome = readonly ImportRun[] | undefined;

/** Прогоны, закрытые не раньше, чем мы спросили. Сервер кладёт в ответ все волны проекта за сутки
 *  любого возраста, и вчерашний APPLIED по статусу неотличим от только что дописанного.
 *  Времена берутся с ДВУХ машин: `updatedAt` ставит Postgres, заголовок `Date` — HTTP-сервер шлюза.
 *  Допущение: их расхождение меньше длительности нашего же запроса, которой и задана верхняя граница
 *  (монотонные часы клиента). Допущение нарушено в сторону отстающей БД — свежий прогон уйдёт за
 *  границу окна, перечита не будет, имена доедут следующим входом на страницу. Часы браузера не
 *  участвуют намеренно: ушедшие вперёд, они давали бы тот же отказ на каждой такой вкладке. */
function settledSinceRequest(answer: SettleWavesAnswer, elapsedMs: number): readonly ImportRun[] {
  const serverTime = answer.serverTime?.getTime();
  // Часов сервера нет — возраст неизвестен; «значит, старый» здесь тихо съело бы дожатые имена.
  if (serverTime === undefined) return answer.runs;

  return answer.runs.filter(run => {
    const updatedAt = run.updatedAt ? Date.parse(run.updatedAt) : Number.NaN;

    return Number.isNaN(updatedAt) || serverTime - updatedAt <= elapsedMs;
  });
}

/**
 * Досчёт волн имён по требованию экрана: сервер применяет разобранные имена сразу, не дожидаясь крона
 * и тихого окна. Экран открывается в любом исходе — незакрытые волны дожимает крон, как и раньше.
 */
@Injectable({ providedIn: 'root' })
export class NameWaveSettleService {
  private readonly personImport = inject(PersonImportApiService);
  /** Когда по проекту последний раз пришёл ответ, ЗАКРЫВАЮЩИЙ окно. Ключ — проект: соседняя съёмка
   *  о своём досчёте ничего не знает. Монотонные часы: окно меряется длительностью, а не датой. */
  private readonly answeredAt = new Map<string, number>();
  /** Досчёт, который ещё летит: чтение персон редактором ждёт его, а не новый POST. */
  private readonly inFlight = new Map<string, Promise<NameWaveSettleOutcome>>();

  /** Летящий досчёт проекта доехал (как угодно); нечего ждать — сразу. Никогда не отклоняется. */
  settling(projectId: string): Promise<void> {
    const flight = this.inFlight.get(projectId);
    return flight ? flight.then(() => undefined) : Promise.resolve();
  }

  settle(projectId: string): Promise<NameWaveSettleOutcome> {
    const startedAt = performance.now();
    const lastAnswer = this.answeredAt.get(projectId);
    if (lastAnswer !== undefined && startedAt - lastAnswer < SETTLE_MEMO_WINDOW_MS) {
      return Promise.resolve(undefined);
    }

    const flight = this.request(projectId, startedAt);
    this.inFlight.set(projectId, flight);
    void flight.finally(() => {
      if (this.inFlight.get(projectId) === flight) this.inFlight.delete(projectId);
    });
    return flight;
  }

  private request(projectId: string, startedAt: number): Promise<NameWaveSettleOutcome> {
    return firstValueFrom(
      // Метка ожидаемого 403 живёт на этом вызове, а не на транспорте: маршрут заведомо откажет
      // организатору и демо-проекту, а прочим вызывающим ту же ручку 403 обязан оставаться следом
      // возможной смены identity.
      this.personImport.settleWavesWithServerClock(projectId, { expectedForbidden: true }).pipe(
        timeout(SETTLE_CEILING_MS),
        map(answer => {
          const answeredAt = performance.now();
          // Окно закрывает только ответ, в котором волн в работе не осталось: пока сервер пишет,
          // следующий вход обязан спросить снова — он и увидит дописанные имена. Пустой ответ окно
          // закрывает: волн нет вовсе, спрашивать не о чем. Решение принимается по СЫРОМУ ответу —
          // прорежённый по свежести список для этого вопроса уже неполон. Таймаут и отказ сюда не
          // доходят вовсе: отметить ими окно значит отобрать у человека повторный вход.
          if (answer.runs.every(run => SETTLED_RUN_STATUSES.has(run.status))) {
            this.answeredAt.set(projectId, answeredAt);
          }

          return settledSinceRequest(answer, answeredAt - startedAt);
        }),
        // Потолок и транспортный отказ — не «нечего дожимать»: пустой список тут выдал бы одно за другое.
        catchError(() => of(undefined)),
      ),
      // Источник, завершившийся без эмита, бросает EmptyError мимо catchError — у страниц это
      // необработанный reject, у резолвера NavigationError с откатом URL.
      { defaultValue: undefined },
    );
  }
}
